<template>
  <div>
    <PopulationFilterOperatorRow
      v-if="showOperator"
      label="Populations"
      :operator="currentOperator"
      :disabled="true"
    />
    <BittsInput
      v-if="showSearch"
      v-model="internalSearchTerm"
      name="searchTerm"
      placeholder="Search populations"
      size="small"
      type="search"
      class="p-6"
    />
    <BittsDivider class="m-0 p-0" />
    <div
      v-if="filteredTreeData.length"
      class="max-h-[300px] max-w-[600px] overflow-y-scroll p-8"
    >
      <BittsBaseTree
        :node-data="filteredTreeData"
        :expand-all="isSearchActive"
        :get-node-indeterminate="getSegmentNodeIndeterminate"
        @update="onTreeUpdate"
      >
        <template
          #node-checkbox="{ node, checked, indeterminate, onInput, disabled }"
        >
          <BittsTooltip
            v-if="isSavedFilter && typeof node.id === 'string'"
            placement="topLeft"
            overlay-class="max-w-[200px]"
            @click.stop
          >
            <BittsCheckbox
              :aria-label="node.label"
              :checked="checked"
              :indeterminate="indeterminate"
              :disabled="disabled"
              @input="onInput"
            />
            <template #title
              >If checked, new populations will be included
              automatically</template
            >
          </BittsTooltip>
          <BittsCheckbox
            v-else
            :aria-label="node.label"
            :checked="checked"
            :indeterminate="indeterminate"
            :disabled="disabled"
            @input="onInput"
            @click.stop
          />
        </template>
        <template #node-suffix="{ node }">
          <BittsCount
            v-if="node.children"
            :count="node.children.filter((c) => c.checked).length"
            :show-zero="true"
            type="neutral"
          />
        </template>
      </BittsBaseTree>
    </div>
    <div v-else class="py-8 text-center">
      <BittsEmptyState
        :font-awesome-icon="['fak', 'search']"
        :font-awesome-icon-style="{ height: '26px', width: '26px' }"
        font-awesome-icon-class="population-filter-tree__empty-state-icon"
        title="No results"
        description="No populations found"
        class="w-[280px] border-none mx-auto"
      />
    </div>
    <div
      v-if="showConvertToAdvanced"
      class="border-t border-neutral-border p-6"
    >
      <BittsOptionListItem
        :option="{
          label: 'Convert to advanced filter',
          value: 'convert_to_advanced_filter',
        }"
        :fa-icon="['fak', 'cycle']"
        @click="emit('convert-to-advanced')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsBaseTree,
  BittsCheckbox,
  BittsCount,
  BittsDivider,
  BittsEmptyState,
  BittsInput,
  BittsOptionListItem,
  BittsTooltip,
} from '@crossbeam/bitts';
import { TreeNodeType } from '@crossbeam/bitts/src/BittsTree/BittsBaseTree.vue';

import { cloneDeep } from 'lodash';
import { computed, ref } from 'vue';

import { SEGMENT_TYPE_MAP } from '@/constants/reports';
import { usePopulationsStore } from '@/stores';
import { Population, SegmentType } from '@/types/populations';
import {
  ReportOurSegmentFilters,
  ReportOurSegmentFiltersItem,
} from '@/types/reports';

import PopulationFilterOperatorRow from './PopulationFilterOperatorRow.vue';

type Node = TreeNodeType<Population>;

const props = withDefaults(
  defineProps<{
    selectedFilters?: ReportOurSegmentFilters;
    showSearch?: boolean;
    showConvertToAdvanced?: boolean;
    showOperator?: boolean;
    /**
     * A saved report keeps a ticked segment or partner as "everything in it",
     * new populations included. Turning this off drops the tooltip promising
     * that, and the empty segments and partners that only that promise makes
     * worth ticking.
     */
    isSavedFilter?: boolean;
  }>(),
  {
    selectedFilters: () => ({ items: [], operator: 'OR' }),
    showSearch: true,
    showConvertToAdvanced: false,
    showOperator: false,
    isSavedFilter: true,
  },
);

const emit = defineEmits<{
  update: [value: ReportOurSegmentFilters];
  'convert-to-advanced': [];
}>();

const popsStore = usePopulationsStore();

const internalSearchTerm = ref('');
const isSearchActive = computed(
  () => internalSearchTerm.value.trim().length > 0,
);

const currentOperator = computed<'OR' | 'AND'>(
  () => props.selectedFilters?.operator ?? 'OR',
);

const SEGMENT_LABELS: Record<SegmentType, string> = {
  customers: 'Customers',
  prospects: 'Prospects',
  open_opportunities: 'Open Opportunities',
  other: 'Other',
};

const treeData = computed(() => {
  const segmentTypes = Object.values(SEGMENT_TYPE_MAP) as SegmentType[];

  return segmentTypes
    .map((segmentType) => {
      const segmentPops = popsStore.getPopulationsBySegment(segmentType);

      const { isSegmentAllSelected, segmentSpecificIds } =
        getFilterState(segmentType);

      const childrenNodes = segmentPops.map((pop) => {
        const isChecked =
          isSegmentAllSelected || (segmentSpecificIds?.has(pop.id) ?? false);

        return {
          id: pop.id,
          label: pop.name,
          checked: isChecked,
          metaData: pop,
        };
      });

      return {
        id: segmentType,
        label: SEGMENT_LABELS[segmentType],
        checked: isSegmentAllSelected,
        children: childrenNodes,
      };
    })
    .filter(
      (segmentNode) => props.isSavedFilter || segmentNode.children.length,
    );
});

const filteredTreeData = computed(() => {
  const term = internalSearchTerm.value.trim().toLowerCase();
  if (!term) return treeData.value;

  return treeData.value
    .map((segmentNode) => ({
      ...segmentNode,
      children: segmentNode.children?.filter((child) =>
        child.label.toLowerCase().includes(term),
      ),
    }))
    .filter((node) => node.children && node.children.length > 0);
});

function onTreeUpdate(updatedNodes: Node[], updatedNodeId: number | string) {
  if (typeof updatedNodeId === 'string') {
    const segmentType = updatedNodeId as SegmentType;
    const segmentNode = updatedNodes.find((s) => s.id === segmentType);
    if (!segmentNode) return;

    const nextItems = cloneDeep(props.selectedFilters?.items ?? []).filter(
      (f) => f.segment !== segmentType,
    );

    if (segmentNode.checked) {
      nextItems.push({ segment: segmentType });
    }

    emit('update', { operator: currentOperator.value, items: nextItems });
    return;
  }

  const affectedSegmentNode = updatedNodes.find((n) =>
    n.children?.some((c) => c.id === updatedNodeId),
  );
  if (!affectedSegmentNode) return;

  const segmentType = affectedSegmentNode.id as SegmentType;
  const allChildren =
    treeData.value.find((n) => n.id === segmentType)?.children ?? [];

  const { isSegmentAllSelected, segmentSpecificIds } =
    getFilterState(segmentType);
  const mergedSelectedIds = new Set<number>(
    isSegmentAllSelected
      ? allChildren.map((c) => c.id as number)
      : [...(segmentSpecificIds ?? [])],
  );

  for (const child of affectedSegmentNode.children ?? []) {
    if (child.checked) mergedSelectedIds.add(child.id as number);
    else mergedSelectedIds.delete(child.id as number);
  }

  const selectedPopIds = [...mergedSelectedIds];

  const nextItems = cloneDeep(props.selectedFilters?.items ?? []);
  const existingIdx = nextItems.findIndex((f) => f.segment === segmentType);

  if (!selectedPopIds.length) {
    if (existingIdx > -1) nextItems.splice(existingIdx, 1);
  } else {
    const newFilter: ReportOurSegmentFiltersItem = {
      segment: segmentType,
      population_ids: selectedPopIds,
    };

    if (existingIdx > -1) nextItems[existingIdx] = newFilter;
    else nextItems.push(newFilter);
  }

  emit('update', { operator: currentOperator.value, items: nextItems });
}

function getSegmentNodeIndeterminate(node: Node): boolean {
  if (typeof node.id !== 'string') return false;
  const { isSegmentAllSelected, segmentSpecificIds } = getFilterState(
    node.id as SegmentType,
  );
  return !isSegmentAllSelected && !!segmentSpecificIds?.size;
}

function getFilterState(segmentType: SegmentType) {
  const activeFilter = props.selectedFilters?.items?.find(
    (f) => f.segment === segmentType,
  );

  if (!activeFilter) {
    return { isSegmentAllSelected: false, segmentSpecificIds: null };
  }

  const ids = activeFilter.population_ids;

  const isAllSelected = !ids || ids.length === 0;

  return {
    isSegmentAllSelected: isAllSelected,
    segmentSpecificIds: ids ? new Set(ids) : null,
  };
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";

:deep(.ant-scroll-number-only) {
  animation: none;
  transition: none;
}

:deep(.population-filter-tree__empty-state-icon) {
  @apply text-neutral-text-weak p-20 rounded-[36px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      theme(colors.neutral.accent / 0.2) 0%,
      theme(colors.neutral.accent / 0) 100%
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}
</style>
